import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const FooterWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#000000',
  color: theme.palette.common.white,
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(4),
  borderTop: '1px solid rgba(212, 175, 55, 0.3)',
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(3),
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(2.5),
  },
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    color: '#D4AF37',
    fontSize: 20,
  },
}));

const ContactLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  opacity: 0.7,
  marginBottom: '2px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: '#B0B0B0',
  fontWeight: 600,
  [theme.breakpoints.down('md')]: {
    fontSize: '0.7rem',
    paddingLeft: '28px',
  },
}));

const Footer = () => {
  return (
    <FooterWrapper>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="space-between">
          <Grid item xs={12} md={5}>
            <Typography variant="body1" sx={{ 
              mb: { xs: 2, md: 3 }, 
              opacity: 0.9,
              lineHeight: { xs: 1.6, md: 1.8 },
              maxWidth: '400px',
              fontSize: { xs: '0.9rem', md: '1rem' },
              display: { xs: 'none', md: 'block' }
            }}>
              Experience luxury and comfort with our premium chauffeur service. Available 24/7 for your transportation needs across London and the UK.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'flex-end' }
          }}>
            <Typography variant="h6" gutterBottom sx={{ 
              fontWeight: 600, 
              mb: { xs: 1, md: 3 },
              fontSize: { xs: '1rem', md: '1.25rem' },
              display: { xs: 'none', md: 'block' }
            }}>
              Contact Us
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              width: '100%'
            }}>
              <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
                <Grid item xs={6}>
                  <Box sx={{ 
                    '& .MuiTypography-root': { textAlign: 'left' }
                  }}>
                    <ContactLabel>Email Us</ContactLabel>
                    <ContactItem>
                      <EmailIcon />
                      <Typography variant="body2" sx={{ 
                        fontSize: '0.75rem',
                        wordBreak: 'break-word'
                      }}>
                        <Link href="mailto:info@sls-services.com" color="inherit" underline="hover" sx={{ 
                          fontWeight: 500,
                          transition: 'color 0.3s ease',
                          '&:hover': {
                            color: '#D4AF37',
                          },
                        }}>
                          info@sls-services.com
                        </Link>
                      </Typography>
                    </ContactItem>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ '& .MuiTypography-root': { textAlign: 'left' } }}>
                    <ContactLabel>WhatsApp & Mobile</ContactLabel>
                    <ContactItem>
                      <WhatsAppIcon />
                      <Typography variant="body2">
                        <Link href="https://wa.me/447542454026" color="inherit" underline="hover" target="_blank" sx={{ 
                          fontWeight: 500,
                          transition: 'color 0.3s ease',
                          '&:hover': {
                            color: '#D4AF37',
                          },
                        }}>
                          07542454026
                        </Link>
                      </Typography>
                    </ContactItem>
                  </Box>
                  <Box sx={{ 
                    '& .MuiTypography-root': { textAlign: 'left' },
                    mt: 2
                  }}>
                    <ContactLabel>Location</ContactLabel>
                    <ContactItem>
                      <LocationOnIcon />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        London, UK
                      </Typography>
                    </ContactItem>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ 
                display: { xs: 'none', md: 'block' },
                '& .MuiTypography-root': { textAlign: 'right' }
              }}>
                <Box sx={{ mb: 2 }}>
                  <ContactLabel sx={{ textAlign: 'right', width: '100%' }}>WhatsApp & Mobile</ContactLabel>
                  <ContactItem sx={{ justifyContent: 'flex-end' }}>
                    <WhatsAppIcon />
                    <Typography variant="body1">
                      <Link href="https://wa.me/447542454026" color="inherit" underline="hover" target="_blank" sx={{ 
                        fontWeight: 500,
                        transition: 'color 0.3s ease',
                        '&:hover': {
                          color: '#D4AF37',
                        },
                      }}>
                        07542454026
                      </Link>
                    </Typography>
                  </ContactItem>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <ContactLabel sx={{ textAlign: 'right', width: '100%' }}>Email Us</ContactLabel>
                  <ContactItem sx={{ justifyContent: 'flex-end' }}>
                    <EmailIcon />
                    <Typography variant="body1">
                      <Link href="mailto:info@sls-services.com" color="inherit" underline="hover" sx={{ 
                        fontWeight: 500,
                        transition: 'color 0.3s ease',
                        '&:hover': {
                          color: '#D4AF37',
                        },
                      }}>
                        info@sls-services.com
                      </Link>
                    </Typography>
                  </ContactItem>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <ContactLabel sx={{ textAlign: 'right', width: '100%' }}>Location</ContactLabel>
                  <ContactItem sx={{ justifyContent: 'flex-end' }}>
                    <LocationOnIcon />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      London, United Kingdom
                    </Typography>
                  </ContactItem>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ 
          mt: { xs: 3, md: 6 }, 
          pt: { xs: 2, md: 3 }, 
          borderTop: '1px solid rgba(212, 175, 55, 0.2)',
          textAlign: 'center'
        }}>
          <Typography variant="body2" sx={{ 
            opacity: 0.7,
            fontSize: { xs: '0.75rem', md: '0.875rem' }
          }}>
            © {new Date().getFullYear()} SLS Luxury Services. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </FooterWrapper>
  );
};

export default Footer; 